import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { Button, Checkbox, ControlLabel, Divider, HelperText, Stack, Toggle, Typography } from 'src/app/components'
import { InputFormGroup, InputSelectAsync, InputTextField, InputTimePicker } from 'src/app/components/HookForm'
import { DeleteIcon } from 'src/app/components/Icons'
import { getPaymentCategory } from 'src/app/modules/settings/services/PaymentCategory.service'
import { getPaymentMethod } from 'src/app/modules/settings/services/PaymentMethod.service'
import LuckyDrawPrize from './LuckyDrawPrize.page'
import { defaultLuckyDrawPrizeTranx, LengthsType } from '../LuckyDrawForm3.hook'
import { LuckyDrawRuleModel } from 'src/app/modules/campaign/models/LuckyDraw.model'
import { PaymentCategoryModel } from 'src/app/modules/settings/models/PaymentCategory.model'
import { PaymentMethodModel } from 'src/app/modules/settings/models/PaymentMethod.model'
import SelectAllCheckbox from 'src/app/components/Inputs/SelectAllCheckbox/SelectAllCheckbox.page'
import { ChangeEvent, useState } from 'react'
import InputRadioGroupSimple from 'src/app/components/HookForm/InputRadioGroup/InputRadioGroupSimple.page'
import { safeArray } from 'src/app/utils/array-utils'
import { cleanNumber } from 'src/app/components/HookForm/InputTextField/InputTextField.utils'

export interface LuckyDrawTransactionProp {
  storeIndex: number
  id?: string
  prizeRuleLength?: LengthsType
  state?: any
  campaignStatus?: string
}

const dayMaster = ['Everyday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const LuckyDrawTransaction = (props: Readonly<LuckyDrawTransactionProp>) => {
  const controlName = `prize_rules.${props.storeIndex}.prize_transaction_rules`
  const form = useFormContext() // Get the full form object
  const { control, setValue, getValues, clearErrors, reset } = form
  const { fields: rules, append: addRule } = useFieldArray({
    control: control,
    name: `prize_rules.${props.storeIndex}.prize_transaction_rules`,
  })
  const errors = form.formState.errors as any
  const selectedTrx = useWatch({ control, name: controlName })
  const [store_categories_length, set_store_categories_length] = useState(0)

  const deletePrize = (delIndex: number) => {
    const currentData = getValues()
    const newValueTrx = selectedTrx.filter((_: any, index: number) => index !== delIndex)
    const updatedLuckyDrawData = {
      prize_rules: currentData.prize_rules.map((store: LuckyDrawRuleModel, sIndex: number) =>
        sIndex === props.storeIndex
          ? {
            ...store,
            prize_transaction_rules: newValueTrx,
          }
          : store
      ),
    }
    reset(updatedLuckyDrawData)
  }

  const handlePaymentCatChange = (data: PaymentCategoryModel[], index: number) => {
    // Get existing payment categories from original database data, not current form state
    const existing: PaymentCategoryModel[] =
      props.state?.rules?.data?.prize_rules?.[props.storeIndex]?.prize_transaction_rules?.[index]?.payment_categories

    const deleted = (existing ?? []).filter(
      (e: PaymentCategoryModel) => !data.some((v) => v.id === e.id)
    )

    if (deleted.length > 0 && ['ACTIVE', 'INACTIVE'].includes(props.campaignStatus ?? '')) {
      form.setError(`${controlName}.${index}.payment_categories`, {
        message: 'Existing payment category cannot be removed',
        type: 'required',
      })
      // Restore by maintaining original order from database
      const allIds = new Set([...data.map((d: PaymentCategoryModel) => d.id), ...deleted.map((d: PaymentCategoryModel) => d.id)])
      const restoredValue = [
        ...(existing ?? []).filter((e: PaymentCategoryModel) => allIds.has(e.id)), // Keep existing items in original order
        ...data.filter((d: PaymentCategoryModel) => !(existing ?? []).some((e: PaymentCategoryModel) => e.id === d.id)) // Add new items at the end
      ]
      setValue(`${controlName}.${index}.payment_categories`, restoredValue)
    } else {
      const selectedCategoryIds = data?.map((_: PaymentCategoryModel) => _?.id) || []
      setValue(`${controlName}.${index}.payment_category_ids`, selectedCategoryIds)
      form.clearErrors(`${controlName}.${index}.payment_categories`)

      // Only clear payment methods if categories are being reduced compared to database data
      const isReducingCategories = data.length < (existing?.length || 0)

      if (isReducingCategories) {
        // Clear payment methods when removing categories to avoid invalid selections
        setValue(`${controlName}.${index}.payment_methods`, [])
        setValue(`${controlName}.${index}.payment_method_ids`, [])
      }
      // If adding categories, keep existing payment methods
    }
  }

  const handlePaymentMedChange = (data: PaymentMethodModel[], index: number) => {
    // Get existing payment methods from original database data, not current form state
    const existing =
      props.state?.rules?.data?.prize_rules?.[props.storeIndex]?.prize_transaction_rules?.[index]?.payment_method_ids
    const dataId = data?.map((_: PaymentMethodModel) => _?.id)
    const deleted = (existing ?? []).filter(
      (e: PaymentCategoryModel) => !dataId.some((v) => v === e)
    )
    if (deleted?.length > 0 && ['ACTIVE', 'INACTIVE'].includes(props.campaignStatus ?? '')) {
      form.setError(`${controlName}.${index}.payment_methods`, {
        message: 'Existing payment by cannot be removed',
        type: 'required',
      })
      // Restore by maintaining original order from database
      const existingMethods = props.state?.rules?.data?.prize_rules?.[props.storeIndex]?.prize_transaction_rules?.[index]?.payment_methods || []
      const allIds = new Set([...dataId, ...deleted])
      const restoredValue = [
        ...existingMethods.filter((m: PaymentMethodModel) => allIds.has(m.id)), // Keep existing items in original order
        ...data.filter((d: PaymentMethodModel) => !existingMethods.some((m: PaymentMethodModel) => m.id === d.id)) // Add new items at the end
      ]
      setValue(`${controlName}.${index}.payment_methods`, restoredValue)
      setValue(`${controlName}.${index}.payment_method_ids`, restoredValue.map((m: PaymentMethodModel) => m.id))
    } else {
      clearErrors(`${controlName}.${index}.payment_methods`)
      setValue(
        `${controlName}.${index}.payment_method_ids`,
        data?.map((_: PaymentMethodModel) => _?.id)
      )
    }
  }

  const handleSyncCheckedAllStore = async (key: boolean, index: number) => {
    try {
      let tempDataPaymentBy: PaymentMethodModel[] = []
      if (key) {
        const paymentCategoryIds = getValues(
          `prize_rules.${props.storeIndex}.prize_transaction_rules.${index}.payment_category_ids`
        )
        const storeCategoriesResponse = await getPaymentMethod({
          payment_category_ids: paymentCategoryIds,
          statuses: ['ACTIVE'],
          is_dropdown: true,
          limit: 2000,
        })
        const storeCategoriesData =
          storeCategoriesResponse?.data?.response_output?.list?.content ?? []
        tempDataPaymentBy = storeCategoriesData
        set_store_categories_length(storeCategoriesData.length)
      }
      const paymentMethodIds = tempDataPaymentBy.map((item) => item.id)
      setValue(`${controlName}.${index}.payment_method_ids`, paymentMethodIds)
      setValue(`${controlName}.${index}.payment_methods`, tempDataPaymentBy)
      clearErrors(`${controlName}.${index}.payment_methods`)
    } catch (error) {
      console.error('Error in handleSyncCheckedAllStore:', error)
    }
  }

  // cycle everyday
  const handleChangeDay = (ruleIndex: number, values: string[]) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (event.target.checked) {
      if (value === 'EVERYDAY' || values.length >= dayMaster.length - 2) {
        setValue(`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.days`, ['EVERYDAY']);
      } else {
        const days = values.filter((_) => _ !== 'EVERYDAY')
        const _values = [...days, value];
        setValue(`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.days`, _values);
      }
    } else {
      const _values = values.filter((_) => _ !== value)
      setValue(`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.days`, _values);
    }
  }

  const renderDayItem = (index: number) => (item: string) => {
    const value = item.toUpperCase()
    const values = safeArray<string>(getValues(`prize_rules.${props.storeIndex}.prize_transaction_rules.${index}.days`));
    const isChecked = values.includes(value);
    return (
      <Checkbox
        disabled={false}
        checked={isChecked}
        label={item}
        value={value}
        key={item}
        onChange={handleChangeDay(index, values)}
      />
    );
  }

  const getDisabled = (index: number): boolean => {
    const prizeRules = props.prizeRuleLength?.prize_rules
    if (!prizeRules || props.storeIndex === undefined || !prizeRules[props.storeIndex]) {
      return false
    }

    return (prizeRules[props.storeIndex].prize_transaction_rules_length ?? 0) > index
  }

  return (
    <>
      {rules.map((_, ruleIndex) => {
        const isCombineReceipt = Boolean(selectedTrx?.[ruleIndex]?.is_combine_receipt)

        return (
          <Stack key={`rules-${ruleIndex}`} className='p-6 border border-neutral-300 rounded-lg'>
            <div className='flex items-center justify-between'>
              <Typography variant='h5'>Transaction {ruleIndex + 1}</Typography>
              <Button
                disabled={rules.length < 2 || getDisabled(ruleIndex)}
                startIcon={DeleteIcon}
                variant='text'
                color='danger'
                onClick={() => deletePrize(ruleIndex)}
              >
                Delete
              </Button>
            </div>

            <ControlLabel label='Minimum Transaction' required={!isCombineReceipt}>
              <Stack direction='row'>
                <div className='w-full'>
                  <InputTextField
                    placeholder='000000'
                    name={`${controlName}.${ruleIndex}.minimum_transaction`}
                    type='currency'
                    rule={
                      isCombineReceipt
                        ? {
                            validate: (val: any) => {
                              if (val === undefined || val === null || val === '' || val === 0) {
                                return true
                              }
                              const num = typeof val === 'string' ? cleanNumber(val) : val
                              if (num !== undefined && num < 1) {
                                return 'Minimum transaction is 1'
                              }
                              if (num !== undefined && num > 999999999) {
                                return 'Maximum transaction is 999.999.999'
                              }
                              return true
                            },
                          }
                        : {
                            required: 'Field cannot be empty.',
                            min: {
                              value: 1,
                              message: 'Minimum transaction is 1',
                            },
                            max: {
                              value: 999999999,
                              message: 'Maximum transaction is 999.999.999',
                            },
                          }
                    }
                    disabled={getDisabled(ruleIndex)}
                    error={
                      errors?.prize_rules?.[props.storeIndex]?.prize_transaction_rules?.[ruleIndex]
                        ?.minimum_transaction?.message
                    }
                    helperText={
                      errors?.prize_rules?.[props.storeIndex]?.prize_transaction_rules?.[ruleIndex]
                        ?.minimum_transaction?.message
                    }
                  />
                </div>
                <div className='min-w-[188px] h-[42px] content-center'>
                  <Controller
                    control={control}
                    name={`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.is_multiple_entry`}
                    render={({ field }) => (
                      <Checkbox
                        label='Valid for Multiple Entries'
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={isCombineReceipt}
                      />
                    )}
                  />
                </div>
              </Stack>
            </ControlLabel>

            <ControlLabel label='Combine Receipt' required>
              <InputRadioGroupSimple
                name={`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.is_combine_receipt`}
                // disabled={getDisabled(ruleIndex)}
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
                onChange={(value) => {
                  if (value) {
                    setValue(`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.is_multiple_entry`, false);
                    clearErrors(`${controlName}.${ruleIndex}.minimum_transaction`);
                  } else {
                    setValue(`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.min_combine_total_amount`, null);
                    setValue(`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.max_combine_receipt`, null);
                    setValue(`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.is_multiplication_combine_receipt`, false);
                    clearErrors(`${controlName}.${ruleIndex}.min_combine_total_amount`);
                    clearErrors(`${controlName}.${ruleIndex}.max_combine_receipt`);
                  }
                }}
              />
            </ControlLabel>

            {isCombineReceipt && (
            <>
              <ControlLabel label='Max. Number of Combine Receipt' required={true}>
                <InputTextField
                  placeholder='Enter number of combine receipt'
                  name={`${controlName}.${ruleIndex}.max_combine_receipt`}
                  type='number'
                  min={0}
                  disabled={getDisabled(ruleIndex)}
                  rule={{
                    required: 'Field cannot be empty.',
                    min: {
                      value: 1,
                      message: 'Minimum max combine receipt is 1',
                    },
                    max: {
                      value: 999999999,
                      message: 'Maximum max combine receipt is 999.999.999',
                    },
                  }}
                  error={errors?.prize_rules?.[props.storeIndex]?.prize_transaction_rules?.[ruleIndex]?.max_combine_receipt?.message}
                  helperText={errors?.prize_rules?.[props.storeIndex]?.prize_transaction_rules?.[ruleIndex]?.max_combine_receipt?.message}
                />
              </ControlLabel>

              <ControlLabel label='Min. Total Spend Combine Receipt' required={true}>
                <Stack direction='row'>
                  <div className='w-full'>
                    <InputTextField
                      placeholder='000000'
                      name={`${controlName}.${ruleIndex}.min_combine_total_amount`}
                      type='currency'
                      disabled={getDisabled(ruleIndex)}
                      rule={{
                        required: 'Field cannot be empty.',
                        min: {
                          value: 1,
                          message: 'Minimum total amount is 1',
                        },
                        max: {
                          value: 999999999,
                          message: 'Maximum total amount is 999.999.999',
                        },
                      }}
                      error={
                        errors?.prize_rules?.[props.storeIndex]?.prize_transaction_rules?.[ruleIndex]
                          ?.min_combine_total_amount?.message
                      }
                      helperText={
                        errors?.prize_rules?.[props.storeIndex]?.prize_transaction_rules?.[ruleIndex]
                          ?.min_combine_total_amount?.message
                      }
                    />
                  </div>
                  <div className='min-w-[188px] h-[42px] content-center'>
                    <Controller
                      control={control}
                      name={`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.is_multiplication_combine_receipt`}
                      render={({ field }) => (
                        <Checkbox
                          label='Multiplication Applied'
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                  </div>
                </Stack>
              </ControlLabel>
            </>
          )}

          <ControlLabel label='Payment Category' required={true}>
            <InputSelectAsync
              onChangeValue={(e) => handlePaymentCatChange(e as PaymentCategoryModel[], ruleIndex)}
              placeholder='Select Payment Category'
              fetchApi={getPaymentCategory}
              payload={{
                statuses: ['ACTIVE'],
                is_dropdown: true,
                limit: 2000,
              }}
              isMulti={true}
              form={form}
              rule={{ required: true }}
              // disabled={getDisabled(ruleIndex)}
              name={`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.payment_categories`}
              selectOption={(_) => ({
                label: _.payment_category_name!,
                value: _.id,
              })}
            />
          </ControlLabel>
          <SelectAllCheckbox
            label='Select All Payment by'
            onChecked={(_) => {
              handleSyncCheckedAllStore(true, ruleIndex)
            }}
            onUnChecked={() => {
              handleSyncCheckedAllStore(false, ruleIndex)
            }}
            checked={
              form.watch(
                `prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.payment_methods`
              )?.length !== 0 &&
              form.watch(
                `prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.payment_methods`
              )?.length === store_categories_length
            }
            disabled={
              getDisabled(ruleIndex) ||
              form.watch(
                `prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.payment_categories`
              )?.length < 1
            }
            selectOption={(_) => ({
              label: _._!,
              value: _._!,
            })}
          />
          <ControlLabel label='Payment By' required={true}>
            <InputSelectAsync
              onChangeValue={(e) => handlePaymentMedChange(e as PaymentMethodModel[], ruleIndex)}
              placeholder='Select payment by'
              fetchApi={getPaymentMethod}
              payload={{
                payment_category_ids: getValues(
                  `prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.payment_category_ids`
                ),
                statuses: ['ACTIVE'],
                is_dropdown: true,
                limit: 2000,
              }}
              isMulti={true}
              form={form}
              rule={{ required: true }}
              disabled={
                // getDisabled(ruleIndex) ||
                form.watch(
                  `prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.payment_categories`
                )?.length < 1
              }
              name={`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.payment_methods`}
              selectOption={(_) => ({
                label: _.method_name!,
                value: _.id,
              })}
            />
          </ControlLabel>

          <Controller
            name={`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.days`}
            control={control}
            rules={{
              required: 'Field cannot be empty',
            }}
            render={() => (
              <ControlLabel label="Usable Day" required={true}>
                <div>
                  <div className="grid grid-cols-4 gap-4">
                    {dayMaster.map(renderDayItem(ruleIndex))}
                  </div>
                  <HelperText error={!!errors?.prize_rules?.[props.storeIndex]?.prize_transaction_rules?.[ruleIndex]?.days}>
                    {errors?.prize_rules?.[props.storeIndex]?.prize_transaction_rules?.[ruleIndex]?.days?.message}
                  </HelperText>
                </div>
              </ControlLabel>
            )}
          />

          <ControlLabel label="Usable Hour" required={true}>
            <Stack direction="row" items="center">
              <InputTimePicker
                placeholder="Select start time"
                name={`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.start_time`}
                rule={{ required: true }}
              />
              <Divider className="w-[50px] border-t-2" color="primary" />
              <InputTimePicker
                placeholder="Select end time"
                name={`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.end_time`}
                rule={{ required: true }}
              />
            </Stack>
          </ControlLabel>

          <ControlLabel label='Mixable Coupon'>
            <div className='flex items-center gap-5'>
              <InputFormGroup form={form}>
                <Toggle
                  disabled={getDisabled(ruleIndex)}
                  name={`prize_rules.${props.storeIndex}.prize_transaction_rules.${ruleIndex}.is_mixable`}
                  toggle={[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ]}
                />
              </InputFormGroup>
            </div>
          </ControlLabel>

          <ControlLabel label='Prize' required={true}>
            <LuckyDrawPrize
              id={props.id}
              storeIndex={props.storeIndex}
              ruleIndex={ruleIndex}
              prizeRuleLength={props.prizeRuleLength}
            />
          </ControlLabel>
          {rules.length - 1 === ruleIndex && (
            <ControlLabel label=''>
              <Button variant='outlined' onClick={() => addRule(defaultLuckyDrawPrizeTranx)}>
                Add New Transaction
              </Button>
            </ControlLabel>
          )}
        </Stack>
      )
    })}
    </>
  )
}
